/**
 * Show a personalized CTA banner when a dynamic variable matches an expected value.
 * The default .banner-content is temporarily hidden to avoid flicker during variable resolution.
 *
 * Usage:
 *   personalizedCTABanner({ interval: 150, hideMode: 'visibility', timeout: 1600 });
 *
 * @param {Object} [options]
 * @param {number} [options.interval=150]   - Polling interval (ms) between checks.
 * @param {number} [options.timeout=1600]   - Max time (ms) to wait for the variable.
 * @param {'visibility'|'display'} [options.hideMode='visibility'] - How to hide the default banner while waiting.
 */
export function personalizedCTABanner(options = {}) {
  const {
    interval = 150,
    timeout = 1600, // Add a default timeout of 1.6 seconds
    hideMode = 'visibility',
  } = options;

  const ctaBanner = document.querySelector('.banner-content');
  const banners = document.querySelectorAll('.banner-content-personalized');
	const closeButton = document.querySelector('.banner > .close');

  // Exit early if required elements are missing
  if ( banners.length === 0 ) return;

  // Helper to hide/show the default CTA
  const hideDefault = () => {
    if ( !ctaBanner ) { return }
    if (hideMode === 'display') {
      ctaBanner.style.display = 'none';
    } else {
      ctaBanner.style.visibility = 'hidden';
    }
  };

  const showDefault = () => {
    if ( !ctaBanner ) { return }
    if (hideMode === 'display') {
      ctaBanner.style.display = '';
    } else {
      ctaBanner.style.visibility = 'visible';
    }
  };

  const run = async () => {
    // Hide default CTA while evaluating personalized banners
    hideDefault();

    const evaluations = Array.from(banners).map(async (elem) => {
      const variablePath = elem.dataset.variable_path;
      const expectedValue = elem.dataset.expected_value;
      const className = elem.dataset.custom_class;
      if (!variablePath || expectedValue === undefined) return null;

      // Wait for variable to exist, but with a timeout
      const value = await waitForPath(variablePath, { root: window, interval, timeout });

      if (value !== undefined && valuesMatch(value, expectedValue)) {
        if (className && ctaBanner?.parentElement) {
          ctaBanner.parentElement.classList.add(className);
        }
        return elem;
      }
      return null;
    });

    try {
      const results = await Promise.all(evaluations);
      const match = results.find(Boolean) || null;
      
      if (match) {
        if ( ctaBanner ) {
          ctaBanner.style.display = 'none';
        }
		if ( closeButton ) {
			closeButton.hidden = false;
		}
        match.style.display = '';
        match.style.height = '';
      } else {
        // This will now run even if variables are not found
		if ( closeButton ) {
			closeButton.hidden = !ctaBanner;
		}
        showDefault();
      }
    } catch (error) {
      console.error("Error during personalized banner evaluation:", error);
      // Ensure default banner is shown on error
		if ( closeButton ) {
			closeButton.hidden = !ctaBanner;
		}
      showDefault();
    }
  };

  // Run when DOM is ready
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    Promise.resolve().then(run);
  } else {
    window.addEventListener('DOMContentLoaded', run, { once: true });
  }
}

function valuesMatch(value, expectedValue) {
  const actual = String(value).trim();
  const expected = String(expectedValue).trim();

  if (actual === expected) return true;
  if (!actual.startsWith('/') || !expected.startsWith('/')) return false;

  return normalizePath(actual) === normalizePath(expected);
}

function normalizePath(path) {
  if (path === '/') return path;

  const parts = path.match(/^([^?#]*)(.*)$/);
  const pathname = parts[1].replace(/\/+$/, '') || '/';
  return pathname + parts[2];
}

/**
 * Continuously polls for a dot-path to become available, with a timeout.
 * Resolves with the value once found, or undefined if it times out.
 *
 * @param {string} path - Dot-separated path to resolve.
 * @param {Object} [opts]
 * @param {Object} [opts.root=window] - Root object to start from.
 * @param {number} [opts.interval=100] - Polling interval (ms).
 * @param {number} [opts.timeout=3000] - Max time to wait (ms).
 * @returns {Promise<*>} - Resolved value or undefined.
 */
export function waitForPath(path, { root = window, interval = 100, timeout = 3000 } = {}) {
  return new Promise((resolve) => {
    if (!path) {
      resolve(undefined);
      return;
    }

    let intervalId;
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      console.warn(`waitForPath timed out after ${timeout}ms for path: "${path}"`);
      resolve(undefined); // Resolve with undefined on timeout
    }, timeout);

    const check = () => {
      const value = getValueFromPath(path, root);
      if (value !== undefined) {
        clearTimeout(timeoutId);
        clearInterval(intervalId);
        resolve(value);
      }
    };

    intervalId = setInterval(check, interval);
    check(); // Initial check
  });
}

/**
 * Safely resolves a path like "utag.data.page_name" without using eval.
 * Returns undefined if any part of the path does not exist.
 *
 * @param {string} path - Dot-separated path string.
 * @param {object} [root=window] - Root object to traverse.
 * @returns {*} - Resolved value or undefined.
 */
export function getValueFromPath(path, root = window) {
  if (!path) return undefined;
  return path.split('.').reduce((obj, key) => {
    return obj && Object.prototype.hasOwnProperty.call(obj, key) ? obj[key] : undefined;
  }, root);
}
